import { Box, Burger, Group, Kbd, Menu, Text, TextInput, UnstyledButton } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { TenantMark } from '@/modules/auth/TenantMark';
import { HelpButton } from '@/modules/help/HelpButton';
import { NotificationBell } from '@/modules/notifications/NotificationBell';
import { RunningTimerIndicator } from '@/modules/time/RunningTimerIndicator';
import type { ActiveTenant, Me } from '@/shared/api/auth';

import classes from './ConsoleHeader.module.css';
import { Icon } from './Icon';

interface ConsoleHeaderProps {
  tenant: ActiveTenant | null;
  email: string;
  me: Me | undefined;
  /** `isPlatformAdminSession(session)` (ISO-24, `AM-134` point 11): offers "Plataforma" in the user menu. */
  isPlatformAdmin: boolean;
  /** `AM-174`: `/tenant-settings/appearance`'s own fields, `undefined` while it has not answered yet - the mark still tries the image and falls back only on an actual load failure. */
  hasLogo?: boolean;
  logoVersion?: string | null;
  navbarOpened: boolean;
  onToggleNavbar: () => void;
  onSignOut: () => void;
  onOpenSearch: () => void;
}

/**
 * Top bar (13 §3.1): product wordmark, tenant mark, global search, the running clock, the
 * notification bell (13 §6.2) and the user menu. The wordmark's cell is as wide as the side menu and
 * the tenant mark lines up with the page content (`ConsoleHeader.module.css`). The clock only
 * mounts for a session with an active tenant, which is what it reads, and shows only while one runs
 * (pendiente 6); the bell also needs a tenant user behind the session, because the inbox is theirs
 * and a support session has none.
 */
export function ConsoleHeader({
  tenant,
  email,
  me,
  isPlatformAdmin,
  hasLogo,
  logoVersion,
  navbarOpened,
  onToggleNavbar,
  onSignOut,
  onOpenSearch,
}: ConsoleHeaderProps) {
  const { t } = useTranslation(['nav', 'auth', 'common']);
  // A support session has no user behind it (ISO-25.3): both names arrive `null` (07 §1.15).
  const displayName =
    me === undefined ? email : `${me.firstName ?? ''} ${me.lastName ?? ''}`.trim() || email;
  // The inbox is a person's own (`recipient_user_id = current user`, 07 §3.2). A support session has
  // no tenant user (`userId: null`), so `/me/notifications` answers it 403 and the bell would keep
  // asking: it is not mounted at all, rather than shown empty and failing every minute (revisión del
  // manual, ronda 2, punto 7).
  const hasInbox = tenant !== null && tenant.userId !== null;

  return (
    <Group
      h="100%"
      px={{ base: 12, sm: 'md' }}
      justify="space-between"
      wrap="nowrap"
      className={classes.bar}
    >
      <Group h="100%" gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
        <Group h="100%" gap={0} wrap="nowrap" style={{ minWidth: 0 }}>
          <div className={classes.brand}>
            <Burger
              opened={navbarOpened}
              onClick={onToggleNavbar}
              hiddenFrom="sm"
              size="sm"
              aria-label={t('nav:aria.toggleMenu')}
            />
            {/* 18 px on a phone (20 from `sm`): with the clock running, the wordmark shares 390 px
                with the clock, the help, the bell and the user menu, and must fit whole (ronda 4,
                punto 6). */}
            <Text
              fw={600}
              fz={{ base: 18, sm: 20 }}
              c="var(--sl-ink)"
              truncate="end"
              style={{ letterSpacing: 'var(--sl-heading-tracking)', minWidth: 0 }}
              component="span"
              data-wordmark
            >
              Service
              <Text component="span" fw={600} c="steel.5" inherit>
                Ledger
              </Text>
            </Text>
          </div>
          {tenant !== null ? (
            <Group gap={8} wrap="nowrap" visibleFrom="sm" className={classes.tenant}>
              <TenantMark
                name={tenant.tenantName}
                tenantSlug={tenant.tenantSlug}
                hasLogo={hasLogo}
                logoVersion={logoVersion}
              />
              {/* A long name gives way before the bell and the user menu do; the title keeps it whole. */}
              <Text fw={500} size="sm" truncate="end" title={tenant.tenantName}>
                {tenant.tenantName}
              </Text>
            </Group>
          ) : null}
        </Group>
        <TextInput
          visibleFrom="md"
          w={360}
          placeholder={t('nav:search.placeholder')}
          leftSection={<Icon name="search" size={16} />}
          rightSection={<Kbd size="xs">Ctrl K</Kbd>}
          rightSectionWidth={64}
          readOnly
          // Opens on a click, Enter or Space - never on focus: closing the palette returns focus
          // here, and opening on focus reopened it at once, so its close button seemed dead.
          onClick={onOpenSearch}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onOpenSearch();
            }
          }}
          aria-label={t('nav:search.placeholder')}
        />
      </Group>

      <Group wrap="nowrap" className={classes.actions}>
        {tenant !== null ? <RunningTimerIndicator /> : null}
        <HelpButton area="console" />
        {hasInbox ? <NotificationBell /> : null}
        <Menu position="bottom-end" width={260}>
          <Menu.Target>
            <UnstyledButton
              aria-label={t('nav:aria.userMenu')}
              style={{ padding: '4px 8px', borderRadius: 'var(--sl-radius-1)' }}
            >
              <Group gap={8} wrap="nowrap">
                <Icon name="user" />
                <Text size="sm" fw={500} visibleFrom="sm" style={{ whiteSpace: 'nowrap' }}>
                  {displayName}
                </Text>
                <Box component="span" visibleFrom="sm" lh={0}>
                  <Icon name="chevronDown" size={16} />
                </Box>
              </Group>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>{t('auth:menu.signedInAs')}</Menu.Label>
            <Menu.Item disabled>
              <Text size="sm">{displayName}</Text>
              <Text size="xs" c="dimmed">
                {email}
              </Text>
            </Menu.Item>
            <Menu.Item
              component={Link}
              to="/account/security"
              leftSection={<Icon name="shield" size={16} />}
            >
              {t('auth:menu.security')}
            </Menu.Item>
            {isPlatformAdmin ? (
              <>
                <Menu.Divider />
                <Menu.Item
                  component={Link}
                  to="/platform"
                  leftSection={<Icon name="settings" size={16} />}
                >
                  {t('auth:menu.platform')}
                </Menu.Item>
              </>
            ) : null}
            <Menu.Divider />
            <Menu.Item leftSection={<Icon name="logout" size={16} />} onClick={onSignOut}>
              {t('auth:menu.signOut')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}
